import type { AuctisyncSupabase } from './client.js';

/**
 * A bidder's saved search phrases, with an optional daily alert.
 *
 * The design calls this "the one feature that brings a bidder back between
 * sales" — everything else in the app only matters while a sale is running.
 *
 * ⚠️ THE ALERT IS REAL. `notify_saved_search_matches` runs hourly in Postgres
 * and sends through the same notification pipeline as everything else. The
 * toggle here is not decorative, which is the only reason it is allowed to
 * exist: a switch that promises alerts nobody sends is a ghost feature.
 *
 * Scoped per HOUSE. A bidder registered with three houses does not want
 * "milwaukee" from all three in one list, and the notification needs a tenant
 * to attribute itself to.
 */

export interface SavedSearch {
  id: string;
  query: string;
  alertsEnabled: boolean;
  /** Null until the alert has fired once. */
  lastNotifiedAt: string | null;
}

/** Matches the database CHECK, so a refusal happens here rather than as an error. */
export const MIN_QUERY_LENGTH = 2;
export const MAX_QUERY_LENGTH = 100;

export type SaveSearchValidity = { ok: true; query: string } | { ok: false; message: string };

/**
 * Is this worth saving?
 *
 * Returns the TRIMMED, lowercased phrase, because the uniqueness constraint is
 * on the stored text: "Milwaukee" and "milwaukee " would otherwise be two rows
 * that alert twice about the same thing.
 */
export function validateSavedSearch(raw: string): SaveSearchValidity {
  const query = raw.trim().replace(/\s+/g, ' ').toLowerCase();
  if (query.length < MIN_QUERY_LENGTH) {
    return { ok: false, message: 'Type a little more before saving this search.' };
  }
  if (query.length > MAX_QUERY_LENGTH) {
    return { ok: false, message: 'That search is too long to save.' };
  }
  return { ok: true, query };
}

export type SavedSearchesResult =
  | { ok: true; searches: SavedSearch[] }
  | { ok: false; message: string };

export async function fetchSavedSearches(
  supabase: AuctisyncSupabase,
  tenantId: string,
): Promise<SavedSearchesResult> {
  const { data, error } = await supabase
    .from('bidder_saved_searches')
    .select('id, query, alerts_enabled, last_notified_at')
    // RLS restricts to the caller's own rows; no user filter is needed and
    // adding one would imply the policy is not doing its job.
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false });

  if (error) return { ok: false, message: error.message };

  return {
    ok: true,
    searches: (data ?? []).map((raw) => {
      const row = raw as {
        id?: unknown;
        query?: unknown;
        alerts_enabled?: unknown;
        last_notified_at?: unknown;
      };
      return {
        id: typeof row.id === 'string' ? row.id : '',
        query: typeof row.query === 'string' ? row.query : '',
        alertsEnabled: row.alerts_enabled === true,
        lastNotifiedAt: typeof row.last_notified_at === 'string' ? row.last_notified_at : null,
      };
    }),
  };
}

export type MutateResult = { ok: true } | { ok: false; message: string };

/**
 * Save a phrase.
 *
 * `alertsEnabled` defaults FALSE. Saving a search and being signed up for
 * notifications are two different decisions, and quietly making one imply the
 * other is how an app earns a mute.
 */
export async function saveSearch(
  supabase: AuctisyncSupabase,
  params: { userId: string; tenantId: string; raw: string; alertsEnabled?: boolean },
): Promise<MutateResult> {
  const valid = validateSavedSearch(params.raw);
  if (!valid.ok) return { ok: false, message: valid.message };

  const { error } = await supabase.from('bidder_saved_searches').insert({
    user_id: params.userId,
    tenant_id: params.tenantId,
    query: valid.query,
    alerts_enabled: params.alertsEnabled ?? false,
  });

  if (error) {
    // A duplicate is a mis-tap, not a failure. Telling someone their search
    // "could not be saved" when it is already saved is a lie they will act on.
    if (error.code === '23505') return { ok: true };
    return { ok: false, message: error.message };
  }
  return { ok: true };
}

export async function setSearchAlerts(
  supabase: AuctisyncSupabase,
  params: { id: string; enabled: boolean },
): Promise<MutateResult> {
  const { error } = await supabase
    .from('bidder_saved_searches')
    .update({ alerts_enabled: params.enabled })
    .eq('id', params.id);
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

export async function deleteSavedSearch(
  supabase: AuctisyncSupabase,
  id: string,
): Promise<MutateResult> {
  const { error } = await supabase.from('bidder_saved_searches').delete().eq('id', id);
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

/**
 * What the alert toggle promises, in the bidder's words.
 *
 * Shared, because it is a commitment about how often we will interrupt someone
 * — and one surface promising "at most once a day" while another implies live
 * alerts is how trust in notifications goes.
 */
export const SAVED_SEARCH_ALERT_PROMISE =
  'An alert arrives once a day at most, when new lots match — never during a sale you are already bidding in.';
